import { DraftinkElement } from "@draftink/draftink/types";

// Canvas size categories
export enum CanvasSize {
  EMPTY = 0,
  SMALL = 1,    // 1-50 elements
  MEDIUM = 2,   // 50-200 elements
  LARGE = 3,    // 200+ elements
  HUGE = 4      // 1000+ elements
}

// Limits for safe context generation
export const CONTEXT_LIMITS = {
  MAX_CONTEXT_LENGTH: 1000,      // characters for AI prompt
  MAX_ELEMENT_DETAILS: 20,       // detailed elements to include
  MAX_TEXT_SAMPLES: 15,          // text examples to include
  MAX_NEW_ELEMENTS: 100,         // max elements AI can generate
  MAX_TOTAL_ELEMENTS: 5000,      // max total canvas elements
  POSITION_PADDING: 100,         // padding between element groups
};

// Keywords that indicate extension/connection intent
export const EXTENSION_KEYWORDS = [
  "add to", "extend", "connect to", "branch from",
  "continue", "expand", "attach to", "link with",
  "append", "follow", "after", "next to"
];

export interface CanvasContext {
  size: CanvasSize;
  elementCount: number;
  elementTypes: Record<string, number>;
  bounds: { minX: number; minY: number; maxX: number; maxY: number } | null;
  textSamples: string[];
  structure: string;
  hasExistingDiagrams: boolean;
}

export interface ElementBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

// Determine canvas size category
export function getCanvasSize(elementCount: number): CanvasSize {
  if (elementCount === 0) return CanvasSize.EMPTY;
  if (elementCount <= 50) return CanvasSize.SMALL;
  if (elementCount <= 200) return CanvasSize.MEDIUM;
  if (elementCount <= 1000) return CanvasSize.LARGE;
  return CanvasSize.HUGE;
}

// Calculate bounds of elements
export function getElementsBounds(elements: DraftinkElement[]): ElementBounds | null {
  if (elements.length === 0) return null;

  const bounds = elements.reduce((acc, el) => ({
    minX: Math.min(acc.minX, el.x),
    minY: Math.min(acc.minY, el.y),
    maxX: Math.max(acc.maxX, el.x + (el.width || 0)),
    maxY: Math.max(acc.maxY, el.y + (el.height || 0))
  }), {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity
  });

  return {
    ...bounds,
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY,
    centerX: (bounds.minX + bounds.maxX) / 2,
    centerY: (bounds.minY + bounds.maxY) / 2
  };
}

// Analyze canvas and extract context
export function analyzeCanvas(elements: DraftinkElement[]): CanvasContext {
  const size = getCanvasSize(elements.length);
  const bounds = getElementsBounds(elements);
  
  // Count element types
  const elementTypes = elements.reduce((acc, el) => {
    acc[el.type] = (acc[el.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Extract text samples (limit to avoid token overflow)
  const textSamples = elements
    .filter(el => el.type === "text")
    .map((el: any) => el.text)
    .filter(text => text && text.trim())
    .slice(0, CONTEXT_LIMITS.MAX_TEXT_SAMPLES);

  // Determine structure type
  const structure = inferStructureType(elementTypes);

  return {
    size,
    elementCount: elements.length,
    elementTypes,
    bounds,
    textSamples,
    structure,
    hasExistingDiagrams: elements.length > 0
  };
}

// Infer the type of diagram structure
function inferStructureType(elementTypes: Record<string, number>): string {
  const hasArrows = (elementTypes.arrow || 0) > 0;
  const hasDiamonds = (elementTypes.diamond || 0) > 0;
  const hasRectangles = (elementTypes.rectangle || 0) > 0;
  const hasTexts = (elementTypes.text || 0) > 0;
  
  if (hasArrows && hasDiamonds) return "flowchart";
  if (hasArrows && hasRectangles) return "process diagram";
  if (hasTexts && !hasArrows) return "notes or labels";
  if (elementTypes.ellipse > 2) return "mindmap or network";
  
  return "mixed diagram";
}

// Build context string for AI prompt
export function buildContextString(context: CanvasContext): string {
  if (context.size === CanvasSize.EMPTY) {
    return "The canvas is currently empty.";
  }

  let contextStr = `Current canvas contains ${context.elementCount} elements`;
  
  // Add type summary
  const typeSummary = Object.entries(context.elementTypes)
    .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
    .join(', ');
  contextStr += ` (${typeSummary})`;

  // Add structure info
  contextStr += `, forming a ${context.structure}.`;

  // Add text samples for small/medium canvases
  if (context.size <= CanvasSize.MEDIUM && context.textSamples.length > 0) {
    const samples = context.textSamples.slice(0, 5).map(t => `"${t}"`).join(', ');
    contextStr += ` Text elements include: ${samples}.`;
  }

  // Add positioning hint
  contextStr += " When generating new diagrams, consider integrating with or extending the existing elements rather than creating completely separate diagrams.";

  return contextStr;
}

// Check if query indicates extension intent
export function hasExtensionIntent(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  return EXTENSION_KEYWORDS.some(keyword => lowerQuery.includes(keyword));
}

// Calculate smart position for new elements
export function calculateSmartPosition(
  newElementsBounds: ElementBounds | null,
  existingBounds: ElementBounds | null,
  userQuery: string
): { x: number; y: number } {
  // If canvas is empty, center in viewport (approximate)
  if (!existingBounds) {
    return { x: 100, y: 100 };
  }

  // If user wants to extend, position closer
  if (hasExtensionIntent(userQuery)) {
    return {
      x: existingBounds.maxX + CONTEXT_LIMITS.POSITION_PADDING / 2,
      y: existingBounds.centerY - (newElementsBounds?.height || 0) / 2
    };
  }

  // Default: position to the right with standard padding
  return {
    x: existingBounds.maxX + CONTEXT_LIMITS.POSITION_PADDING,
    y: existingBounds.minY
  };
}

// Validate if generated elements are safe to add
export function validateGeneratedElements(
  newElements: DraftinkElement[],
  existingElements: DraftinkElement[]
): { valid: boolean; error?: string } {
  // Check element count
  if (newElements.length > CONTEXT_LIMITS.MAX_NEW_ELEMENTS) {
    return { 
      valid: false, 
      error: `Too many elements generated (${newElements.length}). Maximum allowed is ${CONTEXT_LIMITS.MAX_NEW_ELEMENTS}.`
    };
  }

  // Check total count
  const totalCount = existingElements.length + newElements.length;
  if (totalCount > CONTEXT_LIMITS.MAX_TOTAL_ELEMENTS) {
    return {
      valid: false,
      error: `Adding these elements would exceed the maximum canvas size of ${CONTEXT_LIMITS.MAX_TOTAL_ELEMENTS} elements.`
    };
  }

  // Check bounds
  const newBounds = getElementsBounds(newElements);
  if (newBounds && (newBounds.width > 10000 || newBounds.height > 10000)) {
    return {
      valid: false,
      error: "Generated diagram is too large. Please try a simpler request."
    };
  }

  return { valid: true };
}

// Add debugLog function if not already present
const debugLog = (...args: any[]) => {
  console.log('[resolveElementTargets]', ...args);
};

// Simple resolver: Searches elements by description (e.g., label, type)
export function resolveElementTargets(elements: DraftinkElement[], targetDesc: string): string[] {
  debugLog("Looking for:", targetDesc);
  debugLog("Total elements:", elements.length);
  
  // NEW: Log all elements with text for full visibility
  console.log("[resolveElementTargets] All elements with text:");
  elements.forEach(el => {
    if (el.type === 'text' && 'text' in el && el.text) {
      const normalizedText = normalizeText(el.text as string);
      console.log(`- ID: ${el.id}, Text: "${el.text}" (normalized: "${normalizedText}")` + ('containerId' in el && el.containerId ? ` (bound to ${el.containerId})` : ''));
    } else if (['rectangle', 'diamond', 'ellipse'].includes(el.type)) {
      // Find bound text for shapes
      const boundText = elements.find(t => t.type === 'text' && 'containerId' in t && t.containerId === el.id);
      if (boundText && 'text' in boundText && boundText.text) {
        const normalizedText = normalizeText(boundText.text as string);
        console.log(`- Shape ID: ${el.id}, Bound Text: "${boundText.text}" (normalized: "${normalizedText}")`);
      }
    }
  });

  const results: string[] = [];

  // Parse target description (e.g., "node labeled 'foo'", "arrow from A to B", "from X to Y")
  const labeledMatch = targetDesc.match(/node labeled ['"]?(.+?)['"]?$/i);
  const connectedMatch = targetDesc.match(/from ['"]?(.+?)['"]? to ['"]?(.+?)['"]?$/i);

  if (labeledMatch) {
    const searchText = labeledMatch[1].trim();
    debugLog("Labeled pattern found, searching for:", searchText);

    elements.forEach(el => {
      if (el.type === 'text' && 'text' in el && el.text) {
        const elementText = el.text as string;
        debugLog("Checking text element:", el.id, "text:", elementText);

        if (isTextMatch(elementText, searchText)) {
          // If text is bound to a shape, return the shape ID
          const idToAdd = ('containerId' in el && el.containerId) ? el.containerId : el.id;
          results.push(idToAdd);
          debugLog("Match found:", idToAdd);
        }
      } else if (['rectangle', 'diamond', 'ellipse'].includes(el.type)) {
        const boundText = elements.find(t => t.type === 'text' && 'containerId' in t && t.containerId === el.id);
        if (boundText && 'text' in boundText && boundText.text) {
          const elementText = boundText.text as string;
          debugLog("Checking shape with bound text:", el.id, "text:", elementText);
          
          if (isTextMatch(elementText, searchText)) {
            results.push(el.id);
            debugLog("Match found - shape:", el.id);
          }
        }
      }
    });
  } else if (connectedMatch) {
    const fromTarget = connectedMatch[1].trim();
    const toTarget = connectedMatch[2].trim();
    debugLog("Connected pattern found, from:", fromTarget, "to:", toTarget);

    // Find from elements
    const fromIds: string[] = [];
    elements.forEach(el => {
      if (el.type === 'text' && 'text' in el && el.text) {
        if (isTextMatch(el.text as string, fromTarget)) {
          const idToAdd = ('containerId' in el && el.containerId) ? el.containerId : el.id;
          fromIds.push(idToAdd);
          debugLog("Found 'from' element:", idToAdd, "text:", el.text);
        }
      } else if (['rectangle', 'diamond', 'ellipse'].includes(el.type)) {
        const boundText = elements.find(t => t.type === 'text' && 'containerId' in t && t.containerId === el.id);
        if (boundText && 'text' in boundText && boundText.text && isTextMatch(boundText.text as string, fromTarget)) {
          fromIds.push(el.id);
          debugLog("Found 'from' shape:", el.id, "text:", boundText.text);
        }
      }
    });

    // Find to elements
    const toIds: string[] = [];
    elements.forEach(el => {
      if (el.type === 'text' && 'text' in el && el.text) {
        if (isTextMatch(el.text as string, toTarget)) {
          const idToAdd = ('containerId' in el && el.containerId) ? el.containerId : el.id;
          toIds.push(idToAdd);
          debugLog("Found 'to' element:", idToAdd, "text:", el.text);
        }
      } else if (['rectangle', 'diamond', 'ellipse'].includes(el.type)) {
        const boundText = elements.find(t => t.type === 'text' && 'containerId' in t && t.containerId === el.id);
        if (boundText && 'text' in boundText && boundText.text && isTextMatch(boundText.text as string, toTarget)) {
          toIds.push(el.id);
          debugLog("Found 'to' shape:", el.id, "text:", boundText.text);
        }
      }
    });

    // For connect operation, return the first match for each
    if (fromIds.length > 0 && toIds.length > 0) {
      results.push(fromIds[0], toIds[0]);
      debugLog("Resolved connection IDs:", results);
    } else {
      debugLog("Failed to resolve connection targets:", { fromIds, toIds });
    }
  } else {
    // Try to match the entire description against element text
    const searchText = targetDesc.trim();
    elements.forEach(el => {
      if (el.type === 'text' && 'text' in el && el.text) {
        if (isTextMatch(el.text as string, searchText)) {
          const idToAdd = ('containerId' in el && el.containerId) ? el.containerId : el.id;
          results.push(idToAdd);
          debugLog("General match found:", idToAdd, "text:", el.text);
        }
      } else if (['rectangle', 'diamond', 'ellipse'].includes(el.type)) {
        const boundText = elements.find(t => t.type === 'text' && 'containerId' in t && t.containerId === el.id);
        if (boundText && 'text' in boundText && boundText.text && isTextMatch(boundText.text as string, searchText)) {
          results.push(el.id);
          debugLog("General match found - shape:", el.id, "text:", boundText.text);
        }
      }
    });
  }

  debugLog("Final results:", results);
  return [...new Set(results)];  // Remove duplicates
}

// NEW: Helper to check if text matches (with fuzzy matching)
function isTextMatch(elementText: string, searchText: string): boolean {
  // First try exact match
  if (elementText === searchText) return true;
  
  // Normalize both texts for comparison
  const normalizedElement = normalizeText(elementText);
  const normalizedSearch = normalizeText(searchText);
  
  // Try normalized exact match
  if (normalizedElement === normalizedSearch) return true;
  
  // Try fuzzy match with high similarity
  const similarity = stringSimilarity(normalizedElement, normalizedSearch);
  if (similarity > 0.85) {  // 85% similarity threshold
    console.log(`[resolveElementTargets] Fuzzy match: "${elementText}" ~ "${searchText}" (similarity: ${similarity})`);
    return true;
  }
  
  // Try contains match
  if (normalizedElement.includes(normalizedSearch) || normalizedSearch.includes(normalizedElement)) {
    return true;
  }
  
  return false;
}

// NEW: Helper to get descriptive text for an element (including bound text)
function getElementDescription(el: DraftinkElement, allElements: DraftinkElement[]): string {
  if (el.type === 'text' && 'text' in el && el.text) {
    return normalizeText(el.text as string);
  }
  
  const boundText = allElements.find(t => t.type === 'text' && 'containerId' in t && t.containerId === el.id);
  if (boundText && 'text' in boundText && boundText.text) {
    return normalizeText(boundText.text as string);
  }
  
  return '';
}

// NEW: Improved normalization - preserves Cyrillic characters
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s\u0400-\u04FF]/g, '')  // Remove punctuation but keep Cyrillic
    .replace(/\s+/g, ' ')     // Normalize spaces
    .trim();
}

// NEW: Simple string similarity (Levenshtein distance)
function stringSimilarity(a: string, b: string): number {
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  
  if (longer.length === 0) return 1.0;
  
  const editDistance = levenshteinDistance(shorter, longer);
  return 1.0 - editDistance / longer.length;
}

function levenshteinDistance(s1: string, s2: string): number {
  const costs: number[] = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) {
      costs[s2.length] = lastValue;
    }
  }
  return costs[s2.length];
}

// Helper to find elements by text content
function findElementsByText(elements: DraftinkElement[], text: string): string[] {
  const lowerText = text.toLowerCase().trim();
  console.log("[findElementsByText] Searching for:", text, "->", lowerText);
  
  const results = elements
    .filter(el => {
      if ('text' in el && el.text) {
        const elementText = (el.text as string).toLowerCase().trim();
        console.log("[findElementsByText] Checking text element:", el.id, elementText);
        return elementText === lowerText || elementText.includes(lowerText);
      }
      // Also check text elements bound to this element
      const boundText = elements.find(e => 
        e.type === 'text' && 
        'containerId' in e && 
        e.containerId === el.id
      );
      if (boundText && 'text' in boundText && boundText.text) {
        const boundTextContent = (boundText.text as string).toLowerCase().trim();
        console.log("[findElementsByText] Checking bound text:", el.id, boundTextContent);
        return boundTextContent === lowerText || boundTextContent.includes(lowerText);
      }
      return false;
    })
    .map(el => el.id);
    
  console.log("[findElementsByText] Found:", results);
  return results;
} 